/**
 * tabManager.web.ts
 *
 * Three-layer multi-tab coordination for the Faster Technician web app.
 *
 * Layer 1 — BroadcastChannel tab leadership
 *   Only one tab is "active" at a time.  All others show InactiveTabOverlay.
 *
 * Layer 2 — Auth event broadcasting
 *   SESSION_EXPIRED / SIGNED_OUT / TOKEN_REFRESHED are propagated to every tab
 *   so all tabs react consistently.
 *
 * Layer 3 — Web Locks OIDC guard
 *   Exposed via `requestOidcLock()` so AgencySelectionScreen can prevent
 *   simultaneous signinRedirect calls that cause state-mismatch errors.
 *
 * NOTE: This module is web-only.  It is never imported on native (the .web.ts
 * resolver ensures that).  All browser-API access is guarded so that the
 * Jest test environment (jsdom) does not crash.
 */

// ─── Constants ────────────────────────────────────────────────────────────────

const CHANNEL_NAME = 'faster_tech_tab_sync';
const SESSION_KEY   = 'faster_tech_tab_id';
const OIDC_LOCK     = 'oidc_signin';

// ─── Message types ────────────────────────────────────────────────────────────

export type TabEventType =
  | 'TAB_ACTIVATED'
  | 'TAB_RECLAIM'
  | 'PING'
  | 'PONG'
  | 'SESSION_EXPIRED'
  | 'SIGNED_OUT'
  | 'TOKEN_REFRESHED';

export interface TabMessage {
  type: TabEventType;
  /** The tab that originated the message. */
  fromTabId: string;
}

// ─── State ────────────────────────────────────────────────────────────────────

/**
 * How long (ms) to wait for a PONG reply before assuming no other tab is active.
 * Kept short so the UI doesn't block for long on first open.
 */
const PING_TIMEOUT_MS = 600;

/** Unique ID for this tab, persisted in sessionStorage (isolated per tab). */
function getTabId(): string {
  if (typeof sessionStorage === 'undefined') return 'ssr-tab';
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    // Use the Web Crypto API for a cryptographically random tab ID.
    // crypto.randomUUID() is available in all modern browsers and Node ≥18.
    // Fall back to getRandomValues() for environments that lack randomUUID
    // (e.g. older jsdom versions used in Jest).
    let uid: string;
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      uid = crypto.randomUUID();
    } else if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const buf = new Uint32Array(3);
      crypto.getRandomValues(buf);
      uid = `${buf[0]!.toString(36)}-${buf[1]!.toString(36)}-${buf[2]!.toString(36)}`;
    } else {
      // Last-resort fallback (non-crypto) — only reached in very old environments.
      uid = `${Date.now().toString(36)}-fallback`;
    }
    id = `tab_${uid}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

type InactiveCallback   = () => void;
type ActiveCallback     = () => void;
type AuthEventCallback  = (type: TabEventType) => void;

let channel: BroadcastChannel | null = null;
let _isActive = true;

const inactiveListeners : Set<InactiveCallback>  = new Set();
const activeListeners   : Set<ActiveCallback>    = new Set();
const authListeners     : Set<AuthEventCallback> = new Set();

// ─── Internal helpers ─────────────────────────────────────────────────────────

function notifyInactive() {
  _isActive = false;
  inactiveListeners.forEach(fn => fn());
}

function notifyActive() {
  _isActive = true;
  activeListeners.forEach(fn => fn());
}

function notifyAuth(type: TabEventType) {
  authListeners.forEach(fn => fn(type));
}

function handleMessage(event: MessageEvent<TabMessage>) {
  const msg: TabMessage = event.data;
  const myId = getTabId();

  // Ignore our own reflected messages (some browsers echo them back).
  if (msg.fromTabId === myId) return;

  switch (msg.type) {
    /**
     * Another tab has asserted leadership.
     * If we are currently active, yield to it.
     */
    case 'TAB_ACTIVATED':
      if (_isActive) {
        console.log(
          `[tabManager] Tab ${myId} yielding — tab ${msg.fromTabId} activated`,
        );
        notifyInactive();
      }
      break;

    /**
     * The user pressed "Use This Tab" in another tab — that tab has claimed
     * leadership.  We (the previous active tab) become inactive.
     */
    case 'TAB_RECLAIM':
      if (_isActive) {
        console.log(
          `[tabManager] Tab ${myId} yielding — tab ${msg.fromTabId} reclaimed`,
        );
        notifyInactive();
      }
      break;

    /**
     * A new/refreshed tab is asking "is anyone already active?".
     * If WE are the current active tab, reply with PONG so the new tab
     * knows to show the "Inactive Tab" overlay instead of stealing leadership.
     */
    case 'PING':
      if (_isActive) {
        console.log(
          `[tabManager] Tab ${myId} replying PONG to pinging tab ${msg.fromTabId}`,
        );
        broadcast('PONG');
      }
      break;

    /**
     * We sent a PING on init and received a PONG back, which means another
     * tab is already active. Yield immediately — do NOT assert TAB_ACTIVATED.
     */
    case 'PONG':
      console.log(
        `[tabManager] Tab ${myId} received PONG — another tab is active, going inactive`,
      );
      notifyInactive();
      break;

    /**
     * Auth lifecycle events — every tab must react.
     */
    case 'SESSION_EXPIRED':
    case 'SIGNED_OUT':
    case 'TOKEN_REFRESHED':
      console.log(`[tabManager] Auth event received: ${msg.type}`);
      notifyAuth(msg.type);
      break;

    default:
      break;
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Initialise the tab manager.
 * Call once from the app entry-point (index.web.tsx).
 * Safe to call multiple times — re-initialisation is a no-op.
 *
 * PING/PONG handshake:
 *   1. New/refreshed tab broadcasts PING and waits PING_TIMEOUT_MS.
 *   2. If an existing active tab is open, it hears the PING and replies PONG.
 *   3. The pinging tab receives PONG and becomes inactive (shows overlay).
 *   4. If no PONG arrives within the timeout, the tab is alone → asserts TAB_ACTIVATED.
 *
 * This prevents a refreshed tab from unconditionally stealing leadership from
 * a tab that was actively being used.
 */
function init(): void {
  if (typeof BroadcastChannel === 'undefined') return; // SSR / old browsers
  if (channel) return; // already initialised

  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = handleMessage;

  // Optimistically assume we are active. The PONG handler will correct this
  // if another tab responds within PING_TIMEOUT_MS.
  _isActive = true;

  // Politely ask if anyone is already active before asserting leadership.
  broadcast('PING');

  // If no PONG arrives within the timeout, no other tab is active —
  // safely claim leadership by broadcasting TAB_ACTIVATED.
  setTimeout(() => {
    if (_isActive) {
      // Still active means no PONG was received — we are the sole tab.
      broadcast('TAB_ACTIVATED');
      console.log(`[tabManager] No PONG received — tab ${getTabId()} is the active leader`);
    }
    // If _isActive is false here, the PONG handler already called notifyInactive().
  }, PING_TIMEOUT_MS);

  console.log(`[tabManager] Initialised — tabId: ${getTabId()}, PING sent`);
}

/**
 * Tear down the channel.  Call on app unmount (useful in tests).
 */
function destroy(): void {
  channel?.close();
  channel = null;
  inactiveListeners.clear();
  activeListeners.clear();
  authListeners.clear();
}

/**
 * Send a message to all OTHER tabs.
 */
function broadcast(type: TabEventType): void {
  if (!channel) return;
  const msg: TabMessage = { type, fromTabId: getTabId() };
  try {
    channel.postMessage(msg);
  } catch (err) {
    console.warn('[tabManager] broadcast failed:', err);
  }
}

/**
 * Claim leadership for THIS tab (user pressed "Use This Tab").
 * Notifies other tabs, then marks ourselves as active.
 */
function reclaimTab(): void {
  broadcast('TAB_RECLAIM');
  notifyActive();
  console.log(`[tabManager] Tab ${getTabId()} reclaimed as active`);
}

/**
 * Broadcast that the session has expired.
 * All tabs will sign out.
 */
function broadcastSessionExpired(): void {
  broadcast('SESSION_EXPIRED');
}

/**
 * Broadcast that the user has explicitly signed out.
 */
function broadcastSignedOut(): void {
  broadcast('SIGNED_OUT');
}

/**
 * Broadcast that a new token has been issued (e.g. after silent refresh).
 */
function broadcastTokenRefreshed(): void {
  broadcast('TOKEN_REFRESHED');
}

/**
 * Returns whether this tab is currently the active (interactive) tab.
 */
function isActive(): boolean {
  return _isActive;
}

/**
 * Subscribe to this-tab-became-inactive events.
 * Returns an unsubscribe function.
 */
function onInactive(cb: InactiveCallback): () => void {
  inactiveListeners.add(cb);
  return () => inactiveListeners.delete(cb);
}

/**
 * Subscribe to this-tab-became-active events (reclaim).
 * Returns an unsubscribe function.
 */
function onActive(cb: ActiveCallback): () => void {
  activeListeners.add(cb);
  return () => activeListeners.delete(cb);
}

/**
 * Subscribe to auth lifecycle events from OTHER tabs.
 * Returns an unsubscribe function.
 */
function onAuthEvent(cb: AuthEventCallback): () => void {
  authListeners.add(cb);
  return () => authListeners.delete(cb);
}

/**
 * Layer 3 — Web Locks OIDC guard.
 *
 * Attempts to acquire an exclusive lock before running `task`.
 * If the lock is already held (another tab is in the middle of signinRedirect),
 * `task` is NOT run and the function returns false immediately (ifAvailable
 * semantics).
 *
 * Falls back to calling `task` directly in environments where Web Locks are
 * unavailable (old browsers, Jest jsdom).
 *
 * @returns Promise<boolean>  true if the lock was acquired and task ran,
 *                            false if the lock was already held.
 */
async function requestOidcLock(task: () => Promise<void>): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    // Fallback — no lock support; run directly.
    await task();
    return true;
  }

  let acquired = false;

  await navigator.locks.request(
    OIDC_LOCK,
    { ifAvailable: true },
    async (lock) => {
      if (!lock) {
        // Lock was not available — another tab is already doing OIDC.
        console.warn(
          '[tabManager] OIDC lock not available — another tab is signing in',
        );
        return;
      }
      acquired = true;
      await task();
    },
  );

  return acquired;
}

// ─── Exported singleton ───────────────────────────────────────────────────────

export const tabManager = {
  init,
  destroy,
  broadcast,
  reclaimTab,
  broadcastSessionExpired,
  broadcastSignedOut,
  broadcastTokenRefreshed,
  isActive,
  onInactive,
  onActive,
  onAuthEvent,
  requestOidcLock,
  /** Exposed for testing only. */
  _getTabId: getTabId,
};